import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import * as Sentry from "@sentry/angular";
import { Observable, throwError } from "rxjs";
import { PRODUCT_TOKEN } from "src/app/core/product.token";
import { BadEndpointException } from "src/app/errors/bad-endpoint.error";
import { SoftwareProduct } from "src/app/support/product";

const URL_PARSE_BASE = "https://invalid.invalid";

function stripQueryAndHash(url: string): string {
  const withoutQuery: string = url.split("?")[0] ?? url;
  const withoutHash: string = withoutQuery.split("#")[0] ?? withoutQuery;

  return withoutHash;
}

function requestPathEndsWithSlash(urlWithoutQueryHash: string): boolean {
  try {
    const parsed: URL = new URL(urlWithoutQueryHash, URL_PARSE_BASE);
    const pathname: string = parsed.pathname;

    return pathname.length > 1 && pathname.endsWith("/");
  } catch {
    const trimmed: string = urlWithoutQueryHash.trim();

    return trimmed.length > 1 && trimmed.endsWith("/");
  }
}

function callerStackForSentry(): string {
  const stack: string = new Error().stack ?? "";
  const lines: string[] = stack.split("\n");
  const filtered: string[] = lines.filter(
    (line: string) =>
      !line.includes("BadEndpointInterceptor") &&
      !line.includes("bad-endpoint.interceptor")
  );

  return filtered.join("\n").trim();
}

@Injectable()
export class BadEndpointInterceptor implements HttpInterceptor {
  readonly #product = inject(PRODUCT_TOKEN);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {
    switch (this.#product) {
      case SoftwareProduct.PMS:
      case SoftwareProduct.ROCKET: {
        const urlWithoutQuery: string = stripQueryAndHash(req.url);

        if (!requestPathEndsWithSlash(urlWithoutQuery)) {
          return next.handle(req);
        }

        const callerStack: string = callerStackForSentry();
        const error: BadEndpointException = new BadEndpointException(
          req.url,
          req.method,
          callerStack
        );

        Sentry.captureException(error, {
          tags: { bad_endpoint_intercepted: "true" },
          extra: {
            message: "Bad endpoint intercepted",
            url: req.url,
            method: req.method,
            urlWithoutQuery,
            callerStack
          }
        });

        return throwError(() => error);
      }
      default: {
        return next.handle(req);
      }
    }
  }
}
